'use client'

import { useTranslations } from 'next-intl'
import React, { useEffect, useRef, useState } from 'react'

type Props = {
  color: string
}

/**
 * Barra fixa no topo do viewport com largura proporcional ao scroll do documento.
 * Atualização via ref (sem setState em scroll) para evitar ciclos com outros handlers
 * que reagem ao layout e voltam a disparar scroll.
 *
 * Só monta o markup após hidratação (`mounted`) para evitar mismatch com Radix / `aria-hidden`
 * (ex.: Sheet no header) que pode tocar em nós fora do diálogo no mesmo ciclo.
 */
export function PostReadingProgressBar({ color }: Props) {
  const t = useTranslations('Blog')
  const [mounted, setMounted] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const fillRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted) return
    const root = rootRef.current
    const fill = fillRef.current
    if (!root || !fill) return

    const update = () => {
      const docEl = document.documentElement
      const scrollable = docEl.scrollHeight - window.innerHeight
      const ratio = scrollable > 0 ? window.scrollY / scrollable : 0
      const pct = Math.min(100, Math.max(0, ratio * 100))
      fill.style.width = `${pct}%`
      root.setAttribute('aria-valuenow', String(Math.round(pct)))
    }

    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update, { passive: true })
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [color, mounted])

  if (!mounted) {
    return null
  }

  return (
    <div
      ref={rootRef}
      className="pointer-events-none fixed top-0 right-0 left-0 z-50 h-1 bg-muted/50"
      role="progressbar"
      aria-valuenow={0}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={t('readingProgress')}
    >
      <div
        ref={fillRef}
        className="h-full transition-[width] duration-150 ease-out"
        style={{ width: '0%', backgroundColor: color }}
      />
    </div>
  )
}
